import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { attendanceApi } from '@/lib/api';
import { Attendance } from '@/types';
import { formatTime, } from '@/lib/utils';
import { ClockIcon, DocumentArrowDownIcon, CalendarIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

export default function DashboardPage() {
  const { user } = useAuth();

  // Debug user information
  console.log('Dashboard - User:', user);
  console.log('Dashboard - User role:', user?.role);
  console.log('Dashboard - Can manage company:', user?.role === 'ADMIN' || user?.role === 'SUPERADMIN');

  const [todayAttendance, setTodayAttendance] = useState<Attendance | null>(null);
  const [attendanceStatus, setAttendanceStatus] = useState<{ hasCheckedIn: boolean; hasCheckedOut: boolean }>({
    hasCheckedIn: false,
    hasCheckedOut: false
  });
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  // const [downloadingOffer, setDownloadingOffer] = useState(false);

  useEffect(() => {
    scrollTo(0, 0)
    fetchTodayAttendance();
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      const response = await attendanceApi.getTodayAttendance();
      console.log('Today attendance response:', response);
      if (response.success && response.data) {
        // Check if response has the new structure with hasCheckedIn/hasCheckedOut
        const data = response.data as any;
        if (data.hasCheckedIn !== undefined && data.attendance) {
          setTodayAttendance(data.attendance);
          setAttendanceStatus({
            hasCheckedIn: data.hasCheckedIn,
            hasCheckedOut: data.hasCheckedOut
          });
        } else {
          // Fallback for old structure
          setTodayAttendance(data);
          setAttendanceStatus({
            hasCheckedIn: !!data.checkIn,
            hasCheckedOut: !!data.checkOut
          });
        }
      }
    } catch (error) {
      console.error('Error fetching today attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const getLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }

          console.log('Location obtained:', {
            latitude: getLocation.latitude,
            longitude: getLocation.longitude
          });

          const response = await attendanceApi.checkIn(
            getLocation.latitude,
            getLocation.longitude,
            'Current Location'
          );

          console.log('Check-in response:', response);

          if (response.success && response.data) {
            // Update attendance data and status
            const data = response.data as any;
            if (data.hasCheckedIn !== undefined && data.attendance) {
              setTodayAttendance(data.attendance);
              setAttendanceStatus({
                hasCheckedIn: data.hasCheckedIn,
                hasCheckedOut: data.hasCheckedOut
              });
            } else {
              setTodayAttendance(data);
              setAttendanceStatus({
                hasCheckedIn: !!data.checkIn,
                hasCheckedOut: !!data.checkOut
              });
            }
            alert('Check-in successful!');
          } else {
            // Handle error response from API
            console.error('Check-in error:', response.error);
            const errorMessage = response.error || 'Check-in failed. Please try again.';

            // Show detailed error message if distance information is available
            const errorData = response.data as any;
            if (errorData?.currentDistance && errorData?.allowedDistance) {
              alert(`${errorMessage}\n\nCurrent distance: ${errorData.currentDistance.toFixed(2)}m\nAllowed distance: ${errorData.allowedDistance}m`);
            } else {
              alert(errorMessage);
            }
          }
        } catch (error: any) {
          console.error('Check-in exception:', error);
          const errorMessage = error?.response?.data?.error || error?.message || 'Check-in failed. Please try again.';
          alert(errorMessage);
        } finally {
          setCheckingIn(false);
        }
      },
      (error) => {
        // Handle geolocation error
        console.error('Geolocation error:', error);
        alert('Failed to get your location. Please enable location access.');
        setCheckingIn(false);
      },
      {
        enableHighAccuracy: true, // Use GPS if available
        timeout: 15000,           // Wait up to 15 seconds for a location fix
        maximumAge: 0             // Don't use cached location
      }
    );


    //static location checkin code for test   
    // const getLocation={
    //       latitude: 22.5690631,
    //       longitude: 88.4349382
    //     }
    //     console.log('Location obtained:7777777777', {
    //       latitude: getLocation.latitude,
    //       longitude: getLocation.longitude
    //     });
    //       const response = await attendanceApi.checkIn(
    //     getLocation.latitude,
    //     getLocation.longitude,
    //     'Current Location'
    //   );

    //   if (response.success && response.data) {
    //     // Update attendance data and status
    //     const data = response.data as any;
    //     if (data.hasCheckedIn !== undefined && data.attendance) {
    //       setTodayAttendance(data.attendance);
    //       setAttendanceStatus({
    //         hasCheckedIn: data.hasCheckedIn,
    //         hasCheckedOut: data.hasCheckedOut
    //       });
    //     } else {
    //       setTodayAttendance(data);
    //       setAttendanceStatus({
    //         hasCheckedIn: !!data.checkIn,
    //         hasCheckedOut: !!data.checkOut
    //       });
    //     }
    //     alert('Check-in successful!');
    //   }
  };

  const handleCheckOut = async () => {
    setCheckingOut(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const getLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }

          console.log('Location obtained:', {
            latitude: getLocation.latitude,
            longitude: getLocation.longitude
          });

          console.log("Starting check-out process...");

          const response = await attendanceApi.checkOut(
            getLocation.latitude,
            getLocation.longitude,
            'Current Location'
          );

          if (response.success && response.data) {
            // Update attendance data and status
            const data = response.data as any;
            if (data.hasCheckedIn !== undefined && data.attendance) {
              setTodayAttendance(data.attendance);
              setAttendanceStatus({
                hasCheckedIn: data.hasCheckedIn,
                hasCheckedOut: data.hasCheckedOut
              });
            } else {
              setTodayAttendance(data);
              setAttendanceStatus({
                hasCheckedIn: !!data.checkIn,
                hasCheckedOut: !!data.checkOut
              });
            }
            alert('Check-out successful!');
          } else {
            // Handle error response from API
            console.error('Check-out error:', response.error);
            const errorMessage = response.error || 'Check-out failed. Please try again.';

            // Show detailed error message if distance information is available
            const errorData = response.data as any;
            if (errorData?.currentDistance && errorData?.allowedDistance) {
              alert(`${errorMessage}\n\nCurrent distance: ${errorData.currentDistance.toFixed(2)}m\nAllowed distance: ${errorData.allowedDistance}m`);
            } else {
              alert(errorMessage);
            }
          }
        } catch (error: any) {
          console.error('Check-out exception:', error);
          const errorMessage = error?.response?.data?.error || error?.message || 'Check-out failed. Please try again.';
          alert(errorMessage);
        } finally {
          setCheckingOut(false);
        }
      },
      (error) => {
        // Handle geolocation error
        console.error('Geolocation error:', error);
        alert('Failed to get your location. Please enable location access.');
        setCheckingOut(false);
      })

    //    //static location checkout code for test
    //           const getLocation={
    //       latitude: 22.5690631,
    //       longitude: 88.4349382
    //     }
    //     console.log('Location obtained:', {
    //       latitude: getLocation.latitude,
    //       longitude: getLocation.longitude
    //     });

    //    try {
    //   console.log("Starting check-out process...");

    //   console.log("Location obtained:", location);

    //   const response = await attendanceApi.checkOut(
    //     getLocation.latitude,
    //     getLocation.longitude,
    //     'Current Location'
    //   );

    //   if (response.success && response.data) {
    //     // Update attendance data and status
    //     const data = response.data as any;
    //     if (data.hasCheckedIn !== undefined && data.attendance) {
    //       setTodayAttendance(data.attendance);
    //       setAttendanceStatus({
    //         hasCheckedIn: data.hasCheckedIn,
    //         hasCheckedOut: data.hasCheckedOut
    //       });
    //     } else {
    //       setTodayAttendance(data);
    //       setAttendanceStatus({
    //         hasCheckedIn: !!data.checkIn,
    //         hasCheckedOut: !!data.checkOut
    //       });
    //     }
    //     alert('Check-out successful!');
    //   }
    // } catch (error) {
    //   console.error('Check-out failed:', error);
    //   const errorMessage = error instanceof Error ? error.message : 'Check-out failed. Please try again.';
    //   alert(errorMessage);
    // } finally {
    //   setCheckingOut(false);
    // }


  };

  // const handleDownloadOfferLetter = async () => {
  //   setDownloadingOffer(true);
  //   try {
  //     const blob = await offerLetterApi.download(user?._id);
  //     downloadBlob(blob, `offer-letter-${user?.employeeId}.pdf`);
  //   } catch (error) {
  //     console.error('Download failed:', error);
  //     alert('Download failed. Please try again.');
  //   } finally {
  //     setDownloadingOffer(false);
  //   }
  // };

  console.log(todayAttendance, "090900-=====")
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="text-gray-600">
          Here's what's happening with your attendance today.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-3">
        {/* Attendance Actions - Top Left */}
        <Card className="w-full lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center">
              <ClockIcon className="mr-2 h-5 w-5 text-blue-600" />
              Attendance
            </CardTitle>
            <CardDescription>
              Mark your attendance for today
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {!attendanceStatus.hasCheckedIn ? (
              <Button
                onClick={handleCheckIn}
                disabled={checkingIn}
                size="sm"
                className="w-full bg-green-600 hover:bg-green-700 text-white shadow-md"
              >
                <ClockIcon className="mr-2 h-4 w-4" />
                {checkingIn ? 'Checking In...' : 'Check In'}
              </Button>
            ) : !attendanceStatus.hasCheckedOut ? (
              <Button
                onClick={handleCheckOut}
                disabled={checkingOut}
                size="sm"
                variant="destructive"
                className="w-full shadow-md"
              >
                <ClockIcon className="mr-2 h-4 w-4" />
                {checkingOut ? 'Checking Out...' : 'Check Out'}
              </Button>
            ) : (
              <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700 font-medium">
                  âœ“ Attendance completed for today
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="w-full lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center">
              <DocumentArrowDownIcon className="mr-2 h-5 w-5 text-purple-600" />
              Quick Actions
            </CardTitle>
            <CardDescription>
              Access your important documents and information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* <Button
                onClick={handleDownloadOfferLetter}
                disabled={downloadingOffer}
                variant="outline"
                size="sm"
                className="justify-start shadow-sm hover:shadow-md transition-shadow"
              >
                <DocumentArrowDownIcon className="mr-2 h-4 w-4" />
                {downloadingOffer ? 'Downloading...' : 'Offer Letter'}
              </Button> */}

              <Button
                variant="outline"
                size="sm"
                className="justify-start shadow-sm hover:shadow-md transition-shadow"
                onClick={() => window.location.href = '/leaves'}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                Apply Leave
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="justify-start shadow-sm hover:shadow-md transition-shadow"
                onClick={() => window.location.href = '/attendance'}
              >
                <ClockIcon className="mr-2 h-4 w-4" />
                View History
              </Button>

              {(user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start shadow-sm hover:shadow-md transition-shadow"
                  onClick={() => window.location.href = '/company'}
                >
                  <BuildingOfficeIcon className="mr-2 h-4 w-4" />
                  Company
                </Button>
              )}

              {/* <Button
                variant="outline"
                size="sm"
                className="justify-start shadow-sm hover:shadow-md transition-shadow"
                onClick={() => window.location.href = '/employees'}
              >
                <UserGroupIcon className="mr-2 h-4 w-4" />
                My Profile
              </Button> */}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="flex flex-col gap-6 md:grid md:grid-cols-2 lg:grid-cols-4">
        <Card className="w-full">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <ClockIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {attendanceStatus.hasCheckedIn && !attendanceStatus.hasCheckedOut ? 'Checked In' :
                attendanceStatus.hasCheckedOut ? 'Checked Out' : 'Not Started'}
            </div>
            <p className="text-xs text-muted-foreground">
              Today's status
            </p>
          </CardContent>
        </Card>

        <Card className="w-full">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Check In</CardTitle>
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {todayAttendance?.checkIn ? formatTime(new Date(todayAttendance.checkIn)) : '--:--'}
            </div>
            <p className="text-xs text-muted-foreground">
              Today's check-in time
            </p>
          </CardContent>
        </Card>

        <Card className="w-full">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Check Out</CardTitle>
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {todayAttendance?.checkOut ? formatTime(new Date(todayAttendance.checkOut)) : '--:--'}
            </div>
            <p className="text-xs text-muted-foreground">
              Today's check-out time
            </p>
          </CardContent>
        </Card>

      </div>




    </div>
  );
}
